import { AppConfig } from "./AppConfig.js";
import { ApiCache } from "./ApiCache.js";
import { getAccessToken, redirectLogin } from "../services/LcapiiService.js";

export class ApiService {
  static baseUrl = AppConfig.api.isLocal ? AppConfig.api.localUrl : AppConfig.api.baseUrl;
  static retryAttempts = AppConfig.api.retryAttempts;

  static async get(endpoint, token = null, options = {}) {
    return this.makeRequest("GET", this.baseUrl + "/" + endpoint, null, token, options);
  }

  static async post(endpoint, data, token = null, options = {}) {
    return this.makeRequest("POST", this.baseUrl + "/" + endpoint, data, token, options);
  }

  static async put(endpoint, data, token = null, options = {}) {
    return this.makeRequest("PUT", this.baseUrl + "/" + endpoint, data, token, options);
  }

  static async patch(endpoint, data = null, token = null, options = {}) {
    return this.makeRequest("PATCH", this.baseUrl + "/" + endpoint, data, token, options);
  }

  static async del(endpoint, token = null, options = {}) {
    return this.makeRequest("DELETE", this.baseUrl + "/" + endpoint, null, token, options);
  }

  static async postFormData(endpoint, formData, token = null) {
    return this.makeRequest("POST", this.baseUrl + "/" + endpoint, formData, token);
  }

  // Descargas binarias (PDF, CSV, XLSX) — sin cache, devuelve Blob.
  static async getBlob(endpoint, token = null) {
    const headers = {};
    if (token) this.applyAuthHeader(headers, token);
    const response = await fetch(this.baseUrl + "/" + endpoint, { method: "GET", headers });
    if (!response.ok) throw new Error("HTTP error! status: " + response.status);
    return response.blob();
  }

  static invalidate(endpointContains = "", token = null) {
    ApiCache.invalidate({ urlContains: this.baseUrl + "/" + endpointContains, token });
  }

  // Todas las llamadas van con el JWT de LCApii; la API lo valida contra LCApii.
  static applyAuthHeader(headers, token) {
    headers["Authorization"] = "Bearer " + token;
  }

  // La API responde los errores como un string JSON ("No tenés permiso para esta acción").
  static async errorDesde(response) {
    let message = "HTTP error! status: " + response.status;
    try {
      const body = await response.json();
      if (typeof body === "string" && body) message = body;
      else if (body?.message) message = body.message;
    } catch {
      /* sin body JSON */
    }
    return Object.assign(new Error(message), { status: response.status });
  }

  static async makeRequest(method, url, data = null, token = null, options = {}) {
    const {
      cacheTiempo = false,
      cachePersistent = true,
      forceRefresh = false,
      dedupe = true,
      retry = this.retryAttempts,
    } = options;

    const cache = AppConfig.api.cacheEnabled ? cacheTiempo : false;
    const ttlMs = ApiCache.parseTTL(cache);
    const cacheKey = ApiCache.buildKey({ method, url, data, token });
    const canCache = ApiCache.canCache({ ttlMs, data });

    if (canCache && !forceRefresh) {
      const cached = ApiCache.read(cacheKey);
      if (cached !== null) return cached;
    }

    if (dedupe) {
      const inflight = ApiCache.getInFlight(cacheKey);
      if (inflight) return inflight;
    }

    const requestPromise = (async () => {
      let lastError;
      for (let attempt = 1; attempt <= retry; attempt++) {
        try {
          const headers = {};
          const authToken = token || (await getAccessToken());
          if (authToken) this.applyAuthHeader(headers, authToken);
          let bodyToSend = data;
          if (data && !(data instanceof FormData)) {
            headers["Content-Type"] = "application/json";
            bodyToSend = JSON.stringify(data);
          }
          const response = await fetch(url, { method, headers, body: bodyToSend || null });
          if (response.status === 401 && !AppConfig.lcapii.skipLogin) {
            redirectLogin();
            throw Object.assign(new Error("Sesión vencida"), { status: 401 });
          }
          if (!response.ok) throw await this.errorDesde(response);
          // Varios endpoints (DELETE, PUT) responden 200 con cuerpo vacío.
          const text = await response.text();
          const json = text ? JSON.parse(text) : null;
          if (canCache) ApiCache.write(cacheKey, json, ttlMs, cachePersistent);
          return json;
        } catch (error) {
          lastError = error;
          if (AppConfig.features?.debug) console.warn("Intento " + attempt + " fallo:", error);
          // Los 4xx (sesión, permiso, no encontrado) no se reintentan: la respuesta va a ser la misma.
          if (error.status >= 400 && error.status < 500) break;
          if (attempt === retry) break;
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
      console.error("API Error tras todos los reintentos:", lastError);
      throw lastError;
    })();

    if (dedupe) ApiCache.setInFlight(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      if (dedupe) ApiCache.clearInFlight(cacheKey);
    }
  }
}
